import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { getSeoMeta, makeGetLocationState } from 'selectors';

import { DEFAULT_SEO_DATE_FORMAT, formatParsedDate } from 'utils';

import { useSelectorProps } from './useSelectorProps';

const getUrlState = makeGetLocationState();

export const useSeoMeta = () => {
  const selectorProps = useSelectorProps();
  const { formatMessage, locale } = useIntl();

  const {
    metaTitle,
    metaDescription,
    pageTitle,
    metaText,
    pageDescription,
  } = useSelector(state => getSeoMeta(state, selectorProps));

  const { currency, date } = useSelector(state =>
    getUrlState(state, selectorProps)
  );

  const curr = useSelector(state => {
    if (currency) {
      return state.currency.currencyList.filter(
        el => el.name.toLowerCase() === currency.toLowerCase()
      );
    }

    return '';
  });

  const currentMessage = useMemo(() => {
    if (currency && !date) return '.withCurrency';
    if (date && !currency) return '.withDate';
    if (date && currency) return '.withCurrencyAndDate';

    return '';
  }, [currency, date]);

  const isNbuPage = useMemo(
    () => selectorProps.location.pathname.includes('nbu'),
    [selectorProps]
  );

  const formattedMetaTitle = useMemo(() => {
    if (isNbuPage && !metaTitle) {
      return formatMessage(
        {
          id: `nbuPage.metaTitle${currentMessage}`,
        },
        {
          currency: curr[0]?.nameG || curr[0]?.text,
          date: formatParsedDate(date, 'DD MMMM YYYY', locale) || '',
        }
      );
    }

    return metaTitle;
  }, [metaTitle, isNbuPage, currency, date]);

  const formattedMetaDescription = useMemo(() => {
    if (isNbuPage && !metaDescription) {
      return formatMessage(
        {
          id: `nbuPage.metaDescription${currentMessage}`,
        },
        { currency: curr[0]?.nameG || curr[0]?.text, date: date || '' }
      );
    }

    return metaDescription;
  }, [metaDescription, isNbuPage, currency, date]);

  const formattedPageTitle = useMemo(() => {
    if (isNbuPage && !pageTitle) {
      return formatMessage(
        {
          id: `nbuPage.pageTitle${currentMessage}`,
        },
        {
          currency: curr[0]?.nameG || curr[0]?.text,
          date:
            formatParsedDate(
              date,
              DEFAULT_SEO_DATE_FORMAT,
              selectorProps.intl.locale
            ) || '',
        }
      );
    }

    return pageTitle;
  }, [pageTitle, isNbuPage, currency, date]);

  return {
    metaTitle: formattedMetaTitle,
    metaDescription: formattedMetaDescription,
    pageTitle: formattedPageTitle,
    metaText: metaText || null,
    pageDescription: pageDescription || '',
  };
};

export default useSeoMeta;
